import React, { useState, useEffect, useCallback } from 'react';
import { useBalance } from '../context/BalanceContext';
import { transactionFilterParams } from '../utils/transactionFilterParams';
import { clearTransactionFieldError, fieldErrorFromTransactionResponse, splitTotal, transactionFieldErrors } from '../utils/transactionFormValidation';
import { formatDate, formatFeedTime, localNowDateTime, toDateTimeLocal } from '../utils/formatDateTime';
import apiClient from '../api/apiClient';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import CategoryPicker from './ui/CategoryPicker';
import DatePicker from './ui/DatePicker';
import { useDialog } from './ui/DialogProvider';
import { Plus, Trash2, Edit3, Search } from 'lucide-react';

const getConvertedAmount = (amount, fromLocation, toLocation, currencies) => {
  const fromRate = Number(currencies.find((item) => item.code === fromLocation?.currency)?.rate_to_rub);
  const toRate = Number(currencies.find((item) => item.code === toLocation?.currency)?.rate_to_rub);
  if (!Number(amount) || !fromRate || !toRate) return '';
  return (Math.round((Number(amount) * fromRate / toRate + Number.EPSILON) * 100) / 100).toFixed(2);
};

const emptySplit = () => ({ category_id: '', amount: '', description: '' });

const normalizeSplitsFromTransaction = (splits) => (Array.isArray(splits) ? splits : [])
  .map((split) => ({
    category_id: split.category_id != null ? String(split.category_id) : '',
    amount: split.amount != null ? String(split.amount) : '',
    description: split.description || ''
  }));

const TransactionModal = ({ isOpen, onClose, categories, incomeSources, locations, tags, onSave, transaction, startWithSplit = false }) => {
  const [formData, setFormData] = useState({
    amount: '',
    transfer_amount: '',
    description: '',
    type: 'expense',
    date: localNowDateTime(),
    category_id: '',
    income_source_id: '',
    from_location_id: '',
    to_location_id: '',
    tag_ids: [],
    splits: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    if (isOpen) apiClient.get('/currencies').then((response) => setCurrencies(response.data.data || [])).catch(() => setCurrencies([]));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (transaction) {
        setFormData({
          amount: transaction.amount,
          transfer_amount: transaction.transfer_amount || transaction.amount,
          description: transaction.description || '',
          type: transaction.type,
          date: toDateTimeLocal(transaction.date),
          category_id: transaction.category_id || '',
          income_source_id: transaction.income_source_id || '',
          from_location_id: transaction.from_location_id || '',
          to_location_id: transaction.to_location_id || '',
          tag_ids: transaction.tags?.map(tag => tag.id) ?? transaction.tag_ids ?? [],
          splits: normalizeSplitsFromTransaction(transaction.splits)
        });
      } else {
        setFormData({
          amount: '',
          transfer_amount: '',
          description: '',
          type: 'expense',
          date: localNowDateTime(),
          category_id: '',
          income_source_id: '',
          from_location_id: '',
          to_location_id: '',
          tag_ids: [],
          splits: startWithSplit ? [emptySplit(), emptySplit()] : []
        });
      }
      setError('');
      setFieldErrors({});
    }
  }, [isOpen, transaction, startWithSplit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors((current) => clearTransactionFieldError(current, name));
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (next.type === 'transfer' && (name === 'from_location_id' || name === 'to_location_id')) {
        const from = locations.find((location) => String(location.id) === String(next.from_location_id));
        const to = locations.find((location) => String(location.id) === String(next.to_location_id));
        const converted = getConvertedAmount(next.amount, from, to, currencies);
        if (converted) next.transfer_amount = converted;
      }
      return next;
    });
  };

  const handleSplitChange = (index, field, value) => {
    setFieldErrors((current) => clearTransactionFieldError(current, 'splits'));
    setFormData(prev => {
      const splits = (prev.splits || []).map((split, i) => (i === index ? { ...split, [field]: value } : split));
      return { ...prev, splits };
    });
  };

  const addSplit = () => {
    setFieldErrors((current) => clearTransactionFieldError(current, 'splits'));
    setFormData(prev => ({ ...prev, splits: [...(prev.splits || []), emptySplit()] }));
  };

  const removeSplit = (index) => {
    setFieldErrors((current) => clearTransactionFieldError(current, 'splits'));
    setFormData(prev => ({ ...prev, splits: (prev.splits || []).filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const validationErrors = transactionFieldErrors(formData, categories);
    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      setError('Заполните обязательные поля операции');
      return;
    }
    setLoading(true);

    try {
      const payload = {
        amount: parseFloat(formData.amount),
        description: formData.description,
        type: formData.type,
        date: formData.date,
        tag_ids: formData.tag_ids || []
      };

      if (formData.type === 'expense') {
        const splits = (formData.splits || []).map((split) => ({
          category_id: parseInt(split.category_id),
          amount: Number(split.amount),
          ...(split.description?.trim() ? { description: split.description.trim() } : {})
        }));
        payload.from_location_id = parseInt(formData.from_location_id);
        if (splits.length > 0) {
          // Заголовочная категория необязательна при корректной разбивке (PERS2-78).
          payload.splits = splits;
          if (formData.category_id) payload.category_id = parseInt(formData.category_id);
        } else {
          payload.category_id = parseInt(formData.category_id);
          // При редактировании явно снимаем разбивку, если пользователь удалил все части.
          if (transaction) payload.splits = [];
        }
      } else if (formData.type === 'income') {
        payload.income_source_id = parseInt(formData.income_source_id);
        payload.to_location_id = parseInt(formData.to_location_id);
      } else if (formData.type === 'transfer') {
        payload.from_location_id = parseInt(formData.from_location_id);
        payload.to_location_id = parseInt(formData.to_location_id);
        payload.transfer_amount = parseFloat(formData.transfer_amount);
      }

      if (transaction) {
        await apiClient.put(`/transactions/${transaction.id}`, payload);
      } else {
        await apiClient.post('/transactions', payload);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error creating/updating transaction:', error);
      const message = error.response?.data?.message || 'Ошибка при сохранении операции';
      setFieldErrors(fieldErrorFromTransactionResponse(message));
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filteredCategories = categories;
  const fromLocation = locations.find((location) => String(location.id) === String(formData.from_location_id));
  const toLocation = locations.find((location) => String(location.id) === String(formData.to_location_id));
  const exchangeRate = Number(formData.amount) > 0 && Number(formData.transfer_amount) > 0
    ? Number(formData.transfer_amount) / Number(formData.amount)
    : null;
  const foreignCurrency = fromLocation?.currency === 'RUB' ? toLocation?.currency : fromLocation?.currency;
  const displayedRate = fromLocation?.currency === 'RUB' && toLocation?.currency !== 'RUB'
    ? 1 / exchangeRate
    : exchangeRate;

  // Разбивка (PERS2-78): живой счётчик распределения и блокировка submit.
  const splitSum = splitTotal(formData.splits);
  const splitRemaining = Number(formData.amount || 0) - splitSum;
  const hasSplits = (formData.splits || []).length > 0;
  const splitsMismatch = hasSplits && Math.abs(splitSum - Number(formData.amount || 0)) > 0.01;
  const formatRub = (value) => Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const splitCounterText = hasSplits
    ? `Распределено: ${formatRub(splitSum)} / ${formatRub(formData.amount)} ₽ · осталось ${formatRub(splitRemaining)} ₽`
    : '';

  // PERS2-83: каскадный выбор — части только из дочерних основной категории.
  const headerCategoryId = formData.category_id != null && formData.category_id !== ''
    ? String(formData.category_id)
    : '';
  const splitChildCategories = headerCategoryId
    ? categories.filter((category) => String(category.parent_id) === headerCategoryId)
    : [];
  const splitCategoryById = new Map(categories.map((category) => [String(category.id), category]));
  // Часть, переставшая быть дочерней после смены шапки (или выбранная из
  // другого места) — подсвечивается ошибкой и блокирует сохранение.
  const isSplitCategoryInvalid = (split) => {
    if (!headerCategoryId || !split.category_id) return false;
    const category = splitCategoryById.get(String(split.category_id));
    return !category || !category.parent_id || String(category.parent_id) !== headerCategoryId;
  };
  const splitsHierarchyBroken = hasSplits && (formData.splits || []).some(isSplitCategoryInvalid);
  const splitBlocked = splitsMismatch || splitsHierarchyBroken || (hasSplits && !headerCategoryId);
  const splitBlockedTitle = splitsHierarchyBroken
    ? 'Категория части должна быть дочерней категорией основной категории'
    : hasSplits && !headerCategoryId
      ? 'При разбивке укажите основную категорию расхода'
      : splitsMismatch
        ? 'Сумма частей должна равняться сумме операции'
        : undefined;
  const headerCategoryName = headerCategoryId
    ? (categories.find((category) => String(category.id) === headerCategoryId)?.name || '')
    : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
      <div className="max-h-[calc(100vh-3rem)] w-full max-w-2xl overflow-y-auto rounded-xl3 border border-line bg-surface px-6 py-7">
        <div className="flex flex-col gap-4 border-b border-line pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-muted">Операция</p>
            <h3 className="text-2xl font-semibold text-ink">
              {transaction ? 'Редактировать операцию' : 'Добавить операцию'}
            </h3>
          </div>
          <Button type="button" variant="ghost" onClick={onClose}>
            Закрыть
          </Button>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <form noValidate onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Input
              label={formData.type === 'transfer' ? `Сумма списания${fromLocation ? `, ${fromLocation.currency}` : ''}` : 'Сумма'}
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              required
              step="0.01"
              min="0"
              placeholder="0.00"
              error={fieldErrors.amount}
            />

            <Select
              label="Тип операции"
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
            >
              <option value="expense">Расход</option>
              <option value="income">Доход</option>
              <option value="transfer">Перевод</option>
            </Select>
          </div>

          <DatePicker
            label="Дата"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
            error={fieldErrors.date}
          />

          {(formData.type === 'expense' || formData.type === 'transfer') && (
            <Select
              label="Откуда"
              name="from_location_id"
              value={formData.from_location_id}
              onChange={handleChange}
              required={formData.type === 'expense' || formData.type === 'transfer'}
              error={fieldErrors.from_location_id}
            >
              <option value="">Выберите счёт</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>{location.name} ({location.currency})</option>
              ))}
            </Select>
          )}

          {formData.type === 'expense' && (
            <CategoryPicker
              label="Категория расхода"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              categories={filteredCategories}
              placeholder="Выберите категорию"
              required
              error={fieldErrors.category_id}
            />
          )}

          {formData.type === 'expense' && (
            <div className="rounded-2xl border border-line bg-surface p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-muted">Разбивка</p>
                  {hasSplits && (
                    <p
                      className={`mt-1 text-sm tabular-nums ${splitsMismatch ? 'text-red-500' : 'text-muted'}`}
                      data-testid="split-counter"
                    >
                      {splitCounterText}
                    </p>
                  )}
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={addSplit} className="self-start sm:self-auto">
                  + Добавить часть
                </Button>
              </div>

              {!hasSplits && (
                <p className="text-sm text-muted">
                  Одна операция на несколько категорий? Добавьте части и распределите сумму.
                </p>
              )}

              {hasSplits && !headerCategoryId && (
                <p className="text-sm text-red-500" data-testid="splits-header-hint">
                  Сначала выберите категорию расхода
                </p>
              )}

              {hasSplits && headerCategoryId && !splitChildCategories.length && (
                <p className="text-sm text-muted" data-testid="splits-no-children-hint">
                  У категории «{headerCategoryName}» нет дочерних категорий для разбивки
                </p>
              )}

              <div className="space-y-4">
                {(formData.splits || []).map((split, index) => (
                  <div key={index} className="flex items-start gap-3" data-testid={`split-row-${index}`}>
                    <div className="min-w-0 flex-1">
                      <CategoryPicker
                        label={`Категория части ${index + 1}`}
                        name={`split_category_${index}`}
                        value={split.category_id}
                        onChange={(e) => handleSplitChange(index, 'category_id', e.target.value)}
                        categories={splitChildCategories}
                        placeholder="Выберите категорию"
                        required
                        disabled={!headerCategoryId}
                        error={isSplitCategoryInvalid(split)
                          ? 'Категория части должна быть дочерней категорией основной категории'
                          : undefined}
                      />
                    </div>
                    <div className="w-36 shrink-0">
                      <Input
                        label={`Сумма ${index + 1}`}
                        type="number"
                        name={`split_amount_${index}`}
                        value={split.amount}
                        onChange={(e) => handleSplitChange(index, 'amount', e.target.value)}
                        required
                        step="0.01"
                        min="0.01"
                        placeholder="0.00"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSplit(index)}
                      className="mt-8 rounded-xl border border-line p-1.5 text-ink-subtle transition-colors hover:border-red-400 hover:text-red-500"
                      title="Удалить часть"
                      aria-label={`Удалить часть ${index + 1}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>

              {fieldErrors.splits && (
                <p className="mt-3 text-sm text-red-500">{fieldErrors.splits}</p>
              )}
              {splitsHierarchyBroken && (
                <p className="mt-3 text-sm text-red-500" data-testid="splits-hierarchy-error">
                  Категория части должна быть дочерней категорией основной категории
                </p>
              )}
            </div>
          )}

          {formData.type === 'income' && (
            <Select
              label="Категория дохода"
              name="income_source_id"
              value={formData.income_source_id}
              onChange={handleChange}
              required
              error={fieldErrors.income_source_id}
            >
              <option value="">Выберите источник</option>
              {incomeSources.map(source => (
                <option key={source.id} value={source.id}>{source.name}</option>
              ))}
            </Select>
          )}

          {(formData.type === 'income' || formData.type === 'transfer') && (
            <Select
              label="Куда"
              name="to_location_id"
              value={formData.to_location_id}
              onChange={handleChange}
              required={formData.type === 'income' || formData.type === 'transfer'}
              error={fieldErrors.to_location_id}
            >
              <option value="">Выберите счёт</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>{location.name} ({location.currency})</option>
              ))}
            </Select>
          )}

          {formData.type === 'transfer' && (
            <div className="space-y-2">
              <Input
                label={`Сумма зачисления${toLocation ? `, ${toLocation.currency}` : ''}`}
                type="number"
                name="transfer_amount"
                value={formData.transfer_amount}
                onChange={handleChange}
                required
                step="0.01"
                min="0.01"
                placeholder="0.00"
                className="hide-number-controls"
                error={fieldErrors.transfer_amount}
              />
              {exchangeRate && fromLocation && toLocation && fromLocation.currency !== toLocation.currency && (
                <p className="text-sm text-muted">Курс обмена: {fromLocation.currency === 'RUB' || toLocation.currency === 'RUB' ? `1 ${foreignCurrency} = ${displayedRate.toFixed(2)} RUB` : `1 ${fromLocation.currency} = ${displayedRate.toFixed(2)} ${toLocation.currency}`}</p>
              )}
            </div>
          )}

          <Input
            label="Описание"
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Введите описание операции"
          />

          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.24em] text-muted">Теги</label>
            <div className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-muted">
              <button
                type="button"
                className="border-b border-line"
                onClick={() => setFormData(prev => ({ ...prev, tag_ids: tags?.map(tag => tag.id) || [] }))}
                disabled={!tags?.length}
              >
                Выбрать все
              </button>
              <span>/</span>
              <button
                type="button"
                className="border-b border-line"
                onClick={() => setFormData(prev => ({ ...prev, tag_ids: [] }))}
                disabled={!tags?.length}
              >
                Снять все
              </button>
            </div>
            <div className="max-h-48 space-y-2 overflow-y-auto rounded-2xl border border-line bg-surface p-3">
              {!tags?.length && (
                <p className="text-xs text-muted">Нет тегов</p>
              )}
              {tags?.map(tag => {
                const checked = formData.tag_ids.includes(tag.id);
                return (
                  <label
                    key={tag.id}
                    className="flex items-center gap-3 text-sm text-ink-subtle"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setFormData(prev => {
                          const exists = prev.tag_ids.includes(tag.id);
                          return {
                            ...prev,
                            tag_ids: exists
                              ? prev.tag_ids.filter(id => id !== tag.id)
                              : [...prev.tag_ids, tag.id]
                          };
                        });
                      }}
                      className="h-4 w-4 rounded border-line text-accent focus:ring-accent"
                    />
                    {tag.name}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              disabled={splitBlocked || undefined}
              title={splitBlockedTitle}
            >
              {transaction ? 'Сохранить' : 'Добавить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TYPE_BADGE = {
  income:   { label: 'Доход',   cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  expense:  { label: 'Расход',  cls: 'bg-red-500/10 text-red-500' },
  transfer: { label: 'Перевод', cls: 'bg-blue-500/10 text-blue-500' },
  loan_disbursement: { label: 'Получение кредита', cls: 'bg-blue-500/10 text-blue-500' },
  loan_payment: { label: 'Платёж кредита', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
};

const TypeBadge = ({ type }) => {
  const { label, cls } = TYPE_BADGE[type] || TYPE_BADGE.expense;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-medium ${cls}`}>
      {label}
    </span>
  );
};

const Empty = () => <span className="italic text-muted opacity-40">—</span>;

// Initial and minimum column widths (px): Date|Amount|Type|Category|Location|Description|Actions
const INIT_WIDTHS = [82, 115, 70, 95, 95, 100, 68];
const MIN_WIDTHS  = [60,  80, 56, 60,  60,  60, 68];
const COL_HEADERS = ['Дата', 'Сумма', 'Тип', 'Категория', 'Место', 'Описание', ''];

const TransactionList = ({ transactions, categories, incomeSources, locations, loading, onDelete, onEdit }) => {
  const [colWidths, setColWidths] = useState(INIT_WIDTHS);

  // Load saved column widths from user preferences on mount
  useEffect(() => {
    apiClient.get('/preferences')
      .then(res => {
        const saved = res.data?.data?.transactionColWidths;
        if (Array.isArray(saved) && saved.length === INIT_WIDTHS.length) {
          setColWidths(saved);
        }
      })
      .catch(() => {});
  }, []);

  const startResize = (e, colIdx) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[colIdx];
    let finalWidths = colWidths;

    const onMove = (mv) => {
      const newW = Math.max(MIN_WIDTHS[colIdx], startW + mv.clientX - startX);
      finalWidths = colWidths.map((w, i) => i === colIdx ? newW : w);
      setColWidths(finalWidths);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      apiClient.put('/preferences', { transactionColWidths: finalWidths }).catch(() => {});
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const gridCols = colWidths.map(w => `${w}px`).join(' ');

  if (loading) {
    return <div className="py-6 text-sm text-muted">Загрузка операций...</div>;
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted">
        Нет операций. Добавьте первую, чтобы начать отслеживание.
      </div>
    );
  }

  const categoryMap = {};
  categories.forEach(cat => { categoryMap[cat.id] = cat; });

  const incomeSourceMap = {};
  incomeSources.forEach(source => { incomeSourceMap[source.id] = source; });

  const locationMap = {};
  locations.forEach(loc => { locationMap[loc.id] = loc; });

  return (
    <div className="overflow-hidden">
      {/* Resizable column headers */}
      <div
        className="hidden lg:grid gap-2 pb-3 border-b border-line"
        style={{ gridTemplateColumns: gridCols }}
      >
        {COL_HEADERS.map((label, i) => (
          <div key={i} className="relative flex items-center select-none overflow-visible">
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted">{label}</span>
            {/* Resize handle — all columns except the last (Actions) */}
            {i < COL_HEADERS.length - 1 && (
              <div
                className="absolute -right-1 top-0 z-10 flex h-full w-3 cursor-col-resize items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                onMouseDown={(e) => startResize(e, i)}
              >
                <div className="h-4 w-0.5 rounded-full bg-accent/50" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Rows */}
      <div className="divide-y divide-line">
        {transactions.map(transaction => {
          const amountTone = transaction.type === 'income' ? 'text-accent' : 'text-ink';

          const categoryLabel = transaction.category_id
            ? (transaction.category_name || categoryMap[transaction.category_id]?.name)
            : null;
          const sourceLabel = transaction.income_source_id
            ? (transaction.income_source_name || incomeSourceMap[transaction.income_source_id]?.name)
            : null;
          const fromLabel = transaction.from_location_id
            ? locationMap[transaction.from_location_id]?.name
            : null;
          const toLabel = transaction.to_location_id
            ? locationMap[transaction.to_location_id]?.name
            : null;
          const fromCurrency = transaction.from_location_id
            ? locationMap[transaction.from_location_id]?.currency
            : 'RUB';
          const toCurrency = transaction.to_location_id
            ? locationMap[transaction.to_location_id]?.currency
            : 'RUB';

          return (
            <div
              key={transaction.id}
              className="group flex flex-col gap-3 py-4 transition-colors hover:bg-surface/60 lg:grid lg:items-center lg:gap-2"
              style={{ gridTemplateColumns: gridCols }}
            >
              <div className="text-xs uppercase tracking-[0.26em] text-muted">
                <div>{formatDate(transaction.date)}</div>
                {formatFeedTime(transaction.date) && (
                  <div className="text-[10px] text-ink-subtle/70 normal-case">{formatFeedTime(transaction.date)}</div>
                )}
              </div>

              <div className={`text-xl font-semibold tabular-nums ${amountTone}`}>
                {Number(transaction.amount).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {fromCurrency}
                {transaction.type === 'transfer' && (
                  <span className="block text-xs font-medium text-muted">→ {Number(transaction.transfer_amount ?? transaction.amount).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {toCurrency}</span>
                )}
              </div>

              <div><TypeBadge type={transaction.type} /></div>

              <div
                className="min-w-0 truncate text-sm text-ink-subtle"
                title={transaction.splits?.length > 0
                  ? transaction.splits.map(split => `${split.category_name || `#${split.category_id}`}: ${Number(split.amount).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`).join('\n')
                  : (categoryLabel || sourceLabel || '')}
              >
                {transaction.splits?.length > 0 ? (
                  <span className="inline-flex items-center rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-accent">
                    Разбивка ({transaction.splits.length})
                  </span>
                ) : (categoryLabel || sourceLabel || <Empty />)}
              </div>

              <div className="min-w-0 truncate text-sm text-ink-subtle" title={[fromLabel, toLabel].filter(Boolean).join(' → ')}>
                {fromLabel && toLabel ? <>{fromLabel} → {toLabel}</> : fromLabel || toLabel || <Empty />}
              </div>

              <div className="min-w-0 truncate text-sm text-ink" title={transaction.description || ''}>
                {transaction.description || <Empty />}
              </div>

              {transaction.loan_id ? (
                <span className="text-xs text-muted">В карточке кредита</span>
              ) : <div className="flex items-center gap-1.5 lg:opacity-0 lg:group-hover:opacity-100 lg:transition-opacity">
                <button
                  onClick={() => onEdit(transaction)}
                  className="rounded-xl border border-line p-1.5 text-ink-subtle hover:border-accent hover:text-accent transition-colors"
                  title="Редактировать"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  onClick={() => onDelete(transaction.id)}
                  className="rounded-xl border border-line p-1.5 text-ink-subtle hover:border-red-400 hover:text-red-500 transition-colors"
                  title="Удалить"
                >
                  <Trash2 size={13} />
                </button>
              </div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TransactionPage = () => {
  const { triggerRefresh } = useBalance();
  const { confirm, notify } = useDialog();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  const [filters, setFilters] = useState({
    type: '',
    category: '',
    incomeSource: '',
    location: '',
    dateFrom: '',
    dateTo: ''
  });

  const [tags, setTags] = useState([]);

  const fetchReferenceData = async () => {
    try {
      const [categoriesRes, incomeSourcesRes, locationsRes, tagsRes] = await Promise.all([
        apiClient.get('/categories'),
        apiClient.get('/income-sources'),
        apiClient.get('/locations'),
        apiClient.get('/tags')
      ]);

      setCategories(categoriesRes.data.data || []);
      setIncomeSources(incomeSourcesRes.data.data || []);
      setLocations(locationsRes.data.data || []);
      setTags(tagsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const fetchTransactions = useCallback(async (page = currentPage) => {
    try {
      setTransactionsLoading(true);
      const response = await apiClient.get('/transactions', {
        params: {
          ...transactionFilterParams(filters, page, pageSize)
        }
      });
      setTransactions(response.data.data || []);
      setCurrentPage(response.data.pagination?.currentPage || page);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setTransactionsLoading(false);
      setLoading(false);
    }
  }, [currentPage, filters, pageSize]);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  useEffect(() => {
    fetchTransactions(currentPage);
  }, [currentPage, fetchTransactions]);

  const handleSave = async () => {
    await fetchTransactions(currentPage);
    setEditingTransaction(null);
    triggerRefresh();
  };

  const handleDelete = async (id) => {
    if (await confirm('Вы уверены, что хотите удалить эту операцию?', { title: 'Удалить операцию?' })) {
      try {
        await apiClient.delete(`/transactions/${id}`);
        await fetchTransactions(currentPage);
        triggerRefresh();
      } catch (error) {
        console.error('Error deleting transaction:', error);
        await notify(error.response?.data?.message || 'Ошибка при удалении операции', { title: 'Не удалось удалить операцию' });
      }
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setShowModal(true);
  };

  const categoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat.id] = cat;
  });

  const incomeSourceMap = {};
  incomeSources.forEach(source => {
    incomeSourceMap[source.id] = source;
  });

  const locationMap = {};
  locations.forEach(loc => {
    locationMap[loc.id] = loc;
  });

  const filteredTransactions = transactions.filter(transaction => {
    if (filters.type && transaction.type !== filters.type) return false;
    if (filters.category && String(transaction.category_id) !== String(filters.category)) {
      // Разбитая операция попадает в фильтр, если её часть относится к категории (PERS2-78).
      const splitMatch = (transaction.splits || []).some(split => String(split.category_id) === String(filters.category));
      if (!splitMatch) return false;
    }
    if (filters.incomeSource && String(transaction.income_source_id) !== String(filters.incomeSource)) return false;
    if (filters.location) {
      const locationId = String(filters.location);
      const fromMatch = String(transaction.from_location_id) === locationId;
      const toMatch = String(transaction.to_location_id) === locationId;
      if (!fromMatch && !toMatch) return false;
    }
    if (filters.dateFrom) {
      const txDate = new Date(transaction.date);
      const fromDate = new Date(filters.dateFrom);
      if (txDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const txDate = new Date(transaction.date);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (txDate > toDate) return false;
    }

    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      transaction.description?.toLowerCase().includes(searchLower) ||
      transaction.amount.toString().includes(searchLower) ||
      (transaction.category_name || categoryMap[transaction.category_id]?.name)?.toLowerCase().includes(searchLower) ||
      (transaction.income_source_name || incomeSourceMap[transaction.income_source_id]?.name)?.toLowerCase().includes(searchLower) ||
      locationMap[transaction.from_location_id]?.name?.toLowerCase().includes(searchLower) ||
      locationMap[transaction.to_location_id]?.name?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) return <div className="text-sm text-muted">Загрузка...</div>;

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      category: '',
      incomeSource: '',
      location: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div className="space-y-10">
      <section className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-4xl font-semibold text-ink">Операции</h2>
        </div>
        <Button onClick={() => setShowModal(true)} icon={Plus}>
          Добавить операцию
        </Button>
      </section>

      <section className="grid gap-8 lg:grid-cols-[420px_1fr]">
        <div className="rounded-xl3 border border-glass-border bg-glass px-6 py-7 backdrop-blur-lg shadow-glass">
          <div className="flex items-center justify-between border-b border-line pb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-muted">Фильтры</p>
              <h3 className="text-2xl font-semibold text-ink">Настройки выборки</h3>
            </div>
            <Button variant="ghost" onClick={clearFilters}>
              Сбросить
            </Button>
          </div>

          <div className="mt-6 space-y-6">
            <Select
              label="Тип операции"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            >
              <option value="">Все типы</option>
              <option value="income">Доход</option>
              <option value="expense">Расход</option>
              <option value="transfer">Перевод</option>
            </Select>

            <CategoryPicker
              label="Категория"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              categories={categories}
              allowEmpty
              emptyLabel="Все категории"
              placeholder="Все категории"
            />

            <Select
              label="Источник дохода"
              value={filters.incomeSource}
              onChange={(e) => handleFilterChange('incomeSource', e.target.value)}
            >
              <option value="">Все источники</option>
              {incomeSources.map(source => (
                <option key={source.id} value={source.id}>{source.name}</option>
              ))}
            </Select>

            <Select
              label="Место хранения"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
            >
              <option value="">Все места</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </Select>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Input
                label="Дата от"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />

              <Input
                label="Дата до"
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="rounded-xl3 border border-glass-border bg-glass px-6 py-7 backdrop-blur-lg shadow-glass">
          <div className="flex flex-col gap-4 border-b border-line pb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-muted">Лента</p>
              <h3 className="text-2xl font-semibold text-ink">Результаты</h3>
            </div>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={16} />
              <input
                type="text"
                placeholder="Поиск внутри результатов"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-2xl border border-line bg-surface py-3 pl-11 pr-4 text-base text-ink outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            {/* Summary bar */}
            {!transactionsLoading && (
              <div className="flex flex-wrap items-center justify-between gap-3 text-xs uppercase tracking-[0.2em] text-muted">
                <span>{filteredTransactions.length} операций</span>
                <div className="flex gap-5">
                  <span>
                    Доходы:{' '}
                    <strong className="text-accent font-semibold">
                      {filteredTransactions
                        .filter(t => t.type === 'income')
                        .reduce((s, t) => s + parseFloat(t.amount), 0)
                        .toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                    </strong>
                  </span>
                  <span>
                    Расходы:{' '}
                    <strong className="text-ink font-semibold">
                      {filteredTransactions
                        .filter(t => t.type === 'expense')
                        .reduce((s, t) => s + parseFloat(t.amount), 0)
                        .toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ₽
                    </strong>
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6">
            <TransactionList
              transactions={filteredTransactions}
              categories={categories}
              incomeSources={incomeSources}
              locations={locations}
              loading={transactionsLoading}
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex flex-col gap-3 text-xs uppercase tracking-[0.24em] text-muted sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="rounded-full border border-line px-4 py-2 disabled:opacity-50"
              >
                Назад
              </button>
              <span>
                Страница {currentPage} из {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="rounded-full border border-line px-4 py-2 disabled:opacity-50"
              >
                Вперед
              </button>
            </div>
          )}
        </div>
      </section>

      <TransactionModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTransaction(null);
        }}
        categories={categories}
        incomeSources={incomeSources}
        locations={locations}
        tags={tags}
        onSave={handleSave}
        transaction={editingTransaction}
      />
    </div>
  );
};

export default TransactionPage;

export { TransactionModal };
